import { takeLatest, call, put, all } from 'redux-saga/effects';
import {
  loadSales,
  loadSalesSuccess,
  loadSalesError,
  loadCredits,
  loadCreditsSuccess,
  loadCreditsError,
  loadDueCollections,
  saveSaleRequest,
  saveSaleStarted,
  saveSaleSucceeded,
  saveSaleFailed,
  settleCreditRequest,
  cancelSaleRequest,
} from './actions';
import { listSales, createSale, deleteSale } from '@/services/sales';
import { listCredits, settleCredit } from '@/services/credits';
import { listDueCollections } from '@/services/dueCollections';
import { loadProducts } from '@/containers/Inventory/actions';
import { loadCustomers } from '@/containers/Customers/actions';
import { loadTasks } from '@/containers/Tasks/actions';
import { getActiveShopId } from '@/utils/shopSession';
import { hydrateSales, hydrateCredits, hydrateDueCollections } from '@/containers/posData/actions';
import type { Sale, Credit, SaleItem, CustomerType, DueCollection } from '@/lib/types';

function mapSale(dto: any): Sale {
  return {
    id: String(dto.id),
    invoiceNumber: dto.invoiceNumber,
    status: (dto.status as Sale['status']) || 'completed',
    totalAmount: Number(dto.totalAmount),
    discountAmount: Number(dto.discountAmount),
    paidAmount: Number(dto.paidAmount),
    paymentMethod: dto.paymentMethod,
    customerId: dto.customerId != null ? String(dto.customerId) : undefined,
    customerName: dto.customerName,
    customerType: dto.customerType as CustomerType | undefined,
    items: dto.items || [],
    tasks: dto.tasks || [],
    createdAt: dto.createdAt,
    cancelledAt: dto.cancelledAt,
  };
}

function mapCredit(dto: any): Credit {
  return {
    id: String(dto.id),
    saleId: String(dto.saleId),
    customerId: String(dto.customerId),
    customerName: dto.customerName,
    customerPhone: dto.customerPhone,
    itemType: dto.itemType as Credit['itemType'],
    itemName: dto.itemName,
    advancePaid: Number(dto.advancePaid),
    amountDue: Number(dto.amountDue),
    status: (dto.status as Credit['status']) || 'outstanding',
    soldAt: dto.soldAt,
    settledAt: dto.settledAt,
  };
}

function mapDueCollection(dto: any): DueCollection {
  return {
    id: String(dto.id),
    creditId: String(dto.creditId),
    saleId: dto.saleId != null ? String(dto.saleId) : undefined,
    customerId: dto.customerId != null ? String(dto.customerId) : undefined,
    customerName: dto.customerName,
    itemName: dto.itemName,
    amount: Number(dto.amount),
    paymentMethod: dto.paymentMethod || 'Cash',
    collectedAt: dto.collectedAt,
    createdAt: dto.createdAt,
  };
}

export function* handleLoadSales(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadSalesError('No active shop selected'));
      return;
    }
    const raw: any[] = yield call(listSales, shopId);
    const sales = raw.map(mapSale);
    yield put(hydrateSales(sales));
    yield put(loadSalesSuccess(sales));
  } catch (e: any) {
    yield put(loadSalesError(e?.message || 'Failed to load sales'));
  }
}

export function* handleLoadCredits(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadCreditsError('No active shop selected'));
      return;
    }
    const raw: any[] = yield call(listCredits, shopId);
    const credits = raw.map(mapCredit);
    yield put(hydrateCredits(credits));
    yield put(loadCreditsSuccess(credits));
  } catch (e: any) {
    yield put(loadCreditsError(e?.message || 'Failed to load credits'));
  }
}

export function* handleLoadDueCollections(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const raw: any[] = yield call(listDueCollections, shopId);
    yield put(hydrateDueCollections(raw.map(mapDueCollection)));
  } catch (e: any) {
    yield put(loadCreditsError(e?.message || 'Failed to load due collections'));
  }
}

export function* handleSaveSale(
  action: ReturnType<typeof saveSaleRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield put(saveSaleStarted());
    const { sale } = action.payload;
    const created: Sale = yield call(createSale, shopId, {
      cart: sale.cart as SaleItem[],
      paymentMethod: sale.paymentMethod,
      total: sale.total,
      discountAmount: (sale as { discountAmount?: number }).discountAmount ?? 0,
      customerId: sale.customerId,
      customerName: sale.customerName,
      customerType: sale.customerType,
      payNow: sale.payNow,
    });
    yield put(saveSaleSucceeded(created));
    yield all([
      put(loadSales()),
      put(loadCredits()),
      put(loadProducts()),
      put(loadCustomers()),
      put(loadTasks()),
    ]);
  } catch (e: any) {
    const message =
      e?.message || 'Failed to save sale. Please try again.';
    yield put(saveSaleFailed(message));
  }
}

export function* handleSettleCredit(
  action: ReturnType<typeof settleCreditRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { id, amount } = action.payload;
    yield call(settleCredit, shopId, Number(id), amount);
    yield all([put(loadCredits()), put(loadDueCollections())]);
  } catch (e: any) {
    yield put(loadCreditsError(e?.message || 'Failed to settle credit'));
  }
}

export function* handleCancelSale(
  action: ReturnType<typeof cancelSaleRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(deleteSale, shopId, Number(action.payload.id));
    yield all([
      put(loadSales()),
      put(loadCredits()),
      put(loadProducts()),
      put(loadCustomers()),
    ]);
  } catch (e: any) {
    yield put(loadSalesError(e?.message || 'Failed to cancel sale'));
  }
}

export default function* saga() {
  yield takeLatest(loadSales.type, handleLoadSales);
  yield takeLatest(loadCredits.type, handleLoadCredits);
  yield takeLatest(loadDueCollections.type, handleLoadDueCollections);
  yield takeLatest(saveSaleRequest.type, handleSaveSale);
  yield takeLatest(settleCreditRequest.type, handleSettleCredit);
  yield takeLatest(cancelSaleRequest.type, handleCancelSale);
}
